/**
 * Date Utilities
 * Centralized date handling functions for the entire application
 */

/**
 * Month names for date formatting
 * @type {string[]}
 */
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Short month names for date formatting
 * @type {string[]}
 */
export const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Day names for date formatting
 * @type {string[]}
 */
export const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

/**
 * Short day names for date formatting
 * @type {string[]}
 */
export const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Get today's date in YYYY-MM-DD format based on Eastern Time (America/New_York).
 * @returns {string} Today's date in YYYY-MM-DD format for ET.
 */
export const getTodayFormatted = () => {
  // Create a date object for the current time
  const now = new Date();

  // Format it to a string representing the date in 'America/New_York' (ET)
  // This handles EST/EDT automatically
  const etDateString = now.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }); // This will be in MM/DD/YYYY format

  // Reformat from MM/DD/YYYY to YYYY-MM-DD
  const parts = etDateString.split('/');
  const year = parts[2];
  const month = parts[0]; // month is 2-digit from toLocaleDateString
  const day = parts[1];   // day is 2-digit from toLocaleDateString

  return `${year}-${month}-${day}`;
};

/**
 * Parse a date string into a Date object
 * @param {string|Date} date - The date string or Date object to parse
 * @returns {Date|null} - The parsed Date object or null if invalid
 */
export const parseDate = (date) => {
  if (!date) return null;
  
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Try to parse the date string
  const parsedDate = new Date(date);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Formats a date string (YYYY-MM-DD) to a more user-friendly format 
 * @param {string|Date} date - The date string or Date object to format
 * @param {string} format - The format to use (default: 'MM/DD/YYYY')
 * @returns {string} - The formatted date string
 */
export const formatDate = (date, format = 'MM/DD/YYYY') => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';
  
  const day = parsedDate.getDate().toString().padStart(2, '0');
  const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
  const year = parsedDate.getFullYear();
  
  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'MMMM DD, YYYY':
      return `${MONTH_NAMES[parsedDate.getMonth()]} ${day}, ${year}`;
    case 'MMM DD, YYYY':
      return `${SHORT_MONTH_NAMES[parsedDate.getMonth()]} ${day}, ${year}`;
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`;
    case 'MM-DD-YYYY':
      return `${month}-${day}-${year}`;
    default:
      return `${month}/${day}/${year}`;
  }
};

/**
 * Format a date with time
 * @param {string|Date} date - The date to format
 * @param {boolean} includeSeconds - Whether to include seconds
 * @returns {string} - The formatted date and time
 */
export const formatDateTime = (date, includeSeconds = false) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';
  
  const dateStr = formatDate(parsedDate);
  
  let hours = parsedDate.getHours();
  const minutes = parsedDate.getMinutes().toString().padStart(2, '0');
  const seconds = parsedDate.getSeconds().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12
  
  return includeSeconds
    ? `${dateStr} ${hours}:${minutes}:${seconds} ${ampm}`
    : `${dateStr} ${hours}:${minutes} ${ampm}`;
};

/**
 * Calculates the number of days between two dates
 * @param {string|Date} startDate - The start date
 * @param {string|Date} endDate - The end date
 * @returns {number} - The number of days between the dates
 */
export const daysBetween = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  
  if (!start || !end) return 0;
  
  // Calculate the difference in days
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Adds a specified number of days to a date
 * @param {string|Date} date - The starting date
 * @param {number} days - Number of days to add
 * @returns {Date} - The new date
 */
export const addDays = (date, days) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;
  
  const result = new Date(parsedDate);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Adds a specified number of months to a date
 * @param {string|Date} date - The starting date
 * @param {number} months - Number of months to add
 * @returns {Date} - The new date
 */
export const addMonths = (date, months) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;
  
  const result = new Date(parsedDate);
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Adds a specified number of years to a date
 * @param {string|Date} date - The starting date
 * @param {number} years - Number of years to add
 * @returns {Date} - The new date
 */
export const addYears = (date, years) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;
  
  const result = new Date(parsedDate);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

/**
 * Checks if a date is today
 * @param {string|Date} date - The date to check
 * @returns {boolean} - True if the date is today
 */
export const isToday = (date) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return false;
  
  const today = new Date();
  return (
    parsedDate.getDate() === today.getDate() &&
    parsedDate.getMonth() === today.getMonth() &&
    parsedDate.getFullYear() === today.getFullYear()
  );
};

/**
 * Get the current date in YYYY-MM-DD format
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const todayISO = () => {
  return formatDate(new Date(), 'YYYY-MM-DD');
};

/**
 * Convert a date to an ISO string without time component
 * @param {string|Date} date - The date to convert
 * @returns {string} - The date in YYYY-MM-DD format
 */
export const toISODate = (date) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';
  
  return formatDate(parsedDate, 'YYYY-MM-DD');
};

/**
 * Returns a relative time string (e.g., "5 minutes ago", "in 2 days")
 * @param {string|Date} date - The date to compare with now
 * @returns {string} - The relative time string
 */
export const getRelativeTimeString = (date) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';
  
  const now = new Date();
  const diffMs = parsedDate - now;
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  
  if (diffSec < 0) {
    // Past
    if (diffSec > -60) return `${Math.abs(diffSec)} seconds ago`;
    if (diffMin > -60) return `${Math.abs(diffMin)} minutes ago`;
    if (diffHour > -24) return `${Math.abs(diffHour)} hours ago`;
    if (diffDay > -30) return `${Math.abs(diffDay)} days ago`;
    return formatDate(parsedDate);
  } else {
    // Future
    if (diffSec < 60) return `in ${diffSec} seconds`;
    if (diffMin < 60) return `in ${diffMin} minutes`;
    if (diffHour < 24) return `in ${diffHour} hours`;
    if (diffDay < 30) return `in ${diffDay} days`;
    return formatDate(parsedDate);
  }
}; 